export type AdminDatePreset = "today" | "yesterday" | "this-week" | "this-month" | "this-year";
export type AdminDateInputField = "from" | "to";
/** A cleared dashboard range must return to a queryable business-day range. */
export const adminDatePickerClearPreset: AdminDatePreset = "today";

type ZonedParts = { year: number; month: number; day: number; hour: number; minute: number; second: number };

function partsInZone(value: Date, timeZone: string): ZonedParts {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit", hourCycle: "h23" }).formatToParts(value);
  const read = (type: string): number => Number(parts.find((part) => part.type === type)?.value ?? 0);
  return { year: read("year"), month: read("month"), day: read("day"), hour: read("hour"), minute: read("minute"), second: read("second") };
}

function dateKey(parts: Pick<ZonedParts, "year" | "month" | "day">): string {
  return `${String(parts.year).padStart(4, "0")}-${String(parts.month).padStart(2, "0")}-${String(parts.day).padStart(2, "0")}`;
}

function addDays(value: string, amount: number): string {
  const date = new Date(`${value}T12:00:00Z`);
  date.setUTCDate(date.getUTCDate() + amount);
  return date.toISOString().slice(0, 10);
}

export function normalizeAdminDateInputRange(fromDate: string, toDate: string, changed: AdminDateInputField): { fromDate: string; toDate: string } {
  if (!fromDate || !toDate || fromDate <= toDate) return { fromDate, toDate };
  return changed === "from" ? { fromDate, toDate: fromDate } : { fromDate: toDate, toDate };
}

function zonedBoundary(date: string, minutes: number, timeZone: string): Date {
  const hour = Math.floor(minutes / 60);
  const minute = minutes % 60;
  const naive = Date.UTC(Number(date.slice(0, 4)), Number(date.slice(5, 7)) - 1, Number(date.slice(8, 10)), hour, minute, 0);
  let result = new Date(naive);
  for (let attempt = 0; attempt < 2; attempt += 1) {
    const local = partsInZone(result, timeZone);
    const localAsUtc = Date.UTC(local.year, local.month - 1, local.day, local.hour, local.minute, local.second);
    result = new Date(naive - (localAsUtc - result.getTime()));
  }
  return result;
}

export function businessDateForInstant(value: Date, timeZone: string, startMinutes: number): string {
  const local = partsInZone(value, timeZone);
  const localDate = dateKey(local);
  return value.getTime() < zonedBoundary(localDate, startMinutes, timeZone).getTime() ? addDays(localDate, -1) : localDate;
}

export function businessDayRange(date: string, timeZone: string, startMinutes: number): { from: string; to: string } {
  return { from: zonedBoundary(date, startMinutes, timeZone).toISOString(), to: zonedBoundary(addDays(date, 1), startMinutes, timeZone).toISOString() };
}

export function adminPresetRange(preset: AdminDatePreset, now: Date, timeZone: string, startMinutes: number): { from: string; to: string; fromDate: string; toDate: string } {
  const current = businessDateForInstant(now, timeZone, startMinutes);
  let start = current;
  if (preset === "yesterday") start = addDays(current, -1);
  if (preset === "this-week") {
    const day = new Date(`${current}T12:00:00Z`).getUTCDay();
    start = addDays(current, -(day === 0 ? 6 : day - 1));
  }
  if (preset === "this-month") start = `${current.slice(0, 8)}01`;
  if (preset === "this-year") start = `${current.slice(0, 4)}-01-01`;
  const endDate = preset === "today" || preset === "yesterday" ? addDays(start, 1) : addDays(current, 1);
  const range = { from: zonedBoundary(start, startMinutes, timeZone).toISOString(), to: zonedBoundary(endDate, startMinutes, timeZone).toISOString() };
  return { ...range, fromDate: start, toDate: addDays(endDate, -1) };
}
